<script setup>

    import InputLabel from '@/Components/Forms/InputLabel.vue'
    import LwxSwitch from '@/Components/Forms/LwxSwitch.vue'
    import LwxCompactSelect from '../Forms/LwxCompactSelect.vue'
    import { computed, ref, watch } from 'vue'
    import { deepCopy, same } from '@/Helpers'

    const emit = defineEmits(['change:careSchedule', 'update:livingPreference'])

    const props = defineProps({
        availability: {
            type: Array,
            default: () => []
        },
        livingPreference: {
            type: String,
            default: ''
        },
        defaultStartHour: {
            type: Number,
            default: 9
        },
        defaultEndHour: {
            type: Number,
            default: 17
        },
        hasMultipleShiftsPerDay: {
            type: Boolean,
            default: false
        },
    })

    const weekDays = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday'
    ]

    const weekends = ['saturday', 'sunday']
    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']

    const formatHour = (hour) => {
        const period = hour < 12 ? 'am' : 'pm'
        const display = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
        return `${display}${period}`
    }

    const hourOptions = Array.from({ length: 24 }, (_, i) => ({
        value: i,
        name: formatHour(i)
    }))

    const defaultState = {
        flexible_fill_in: false,
        alternate_weekends_only: false,
        exact_match: false,
        enable_day: {
            sunday: false,
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false
        },
        start_hour: Object.fromEntries(weekDays.map(d => [d, props.defaultStartHour])),
        end_hour: Object.fromEntries(weekDays.map(d => [d, props.defaultEndHour])),
        has_multiple_shifts_per_day: false
    }

    const state = ref(deepCopy(defaultState))

    const enableWeekdays = () => {
        enableAllDays(weekdays)
        disableDays(weekends)
    }

    const enableWeekends = () => {
        enableDays(weekends)
        disableDays(weekdays)
    }

    const enableAllDays = () => {
        enableDays(weekDays)
        enableDays(weekends)
    }

    const enableDays = (days) => {
        if (scheduleDisable.value) {
            return;
        }
        days.forEach(day => {
            state.value.enable_day[day] = true
        })
    }

    const disableDays = (days) => {
        if (scheduleDisable.value) {
            return
        }

        days.forEach(day => {
            state.value.enable_day[day] = false
        })
    }

    watch(() => props.livingPreference, (newPreference, oldPreference) => {
        if (!newPreference) {
            state.value = deepCopy(defaultState)
        } else if (newPreference === 'live_out' && oldPreference === 'live_in') {
            weekDays.forEach(day => {
                if (state.value.start_hour[day] == null) {
                    state.value.start_hour[day] = props.defaultStartHour
                }
                if (state.value.end_hour[day] == null) {
                    state.value.end_hour[day] = props.defaultEndHour
                }
            })
        }
    })

    watch(() => props.availability, (newAvailability, oldAvailability) => {
        if (same(newAvailability, oldAvailability)) {
            return
        }

        const newState = {
            enable_day: {...defaultState.enable_day},
            start_hour: {...state.value.start_hour},
            end_hour: {...state.value.end_hour},
            flexible_fill_in: state.value.flexible_fill_in,
            alternate_weekends_only: state.value.alternate_weekends_only,
            exact_match: state.value.exact_match
        }

        if (newAvailability.length > 0) {
            newAvailability.forEach(item => {
                newState.enable_day[item.day] = true
                newState.start_hour[item.day] = item.start_hour
                newState.end_hour[item.day] = item.end_hour
            })
        }

        state.value = newState
    }, { immediate: true})

    watch(() => props.hasMultipleShiftsPerDay, (newValue) => {
        state.value.has_multiple_shifts_per_day = newValue
    }, { immediate: true })

    watch(state, (newValue) => {
        const availability = []

        Object.keys(newValue.enable_day).forEach(day => {
            if (newValue.enable_day[day]) {
                availability.push({
                    day: day,
                    start_hour: newValue.start_hour[day],
                    end_hour: newValue.end_hour[day]
                })
            }
        })
        emit('change:careSchedule', {
            flexible_fill_in: newValue.flexible_fill_in,
            alternate_weekends_only: newValue.alternate_weekends_only,
            exact_match: newValue.exact_match,
            availability: availability
        })
    }, { deep: true })

    const timeDisable = computed(() => {
        return !props.livingPreference || props.livingPreference === 'live_in';
    })

    const scheduleDisable = computed(() => {
        return !props.livingPreference;
    })

    defineExpose({ enableWeekdays, enableWeekends, enableAllDays })

</script>

<template>

    <div>
        <div
            v-if="props.livingPreference"
            class="bg-[#008AAB26] rounded-md p-4 border border-lifeworx-blue-600/30">
            <div class="w-full mb-5 grid grid-cols-7 gap-x-2">
                <div v-for="day in weekDays"
                    class="w-full flex flex-col items-center gap-x-1 group">
                    <label
                        :class="[
                            'w-full flex flex-col items-center gap-1 cursor-pointer pb-1.5',
                            {
                                'opacity-50 group-hover:opacity-100': state.enable_day[day] == false
                            }
                    ]">
                        <span class="font-bold">
                            {{ day.charAt(0).toUpperCase() }}
                        </span>
                        <input
                            :id="`${day}_enable`"
                            v-model="state.enable_day[day]"
                            :disabled="scheduleDisable"
                            type="checkbox"
                            :class="[
                                'focus:ring-0 rounded-sm cursor-pointer',
                                {
                                    'text-lifeworx-green-600 checked:bg-lifeworx-green-600': props.livingPreference === 'live_out',
                                    'text-lifeworx-purple-600 checked:bg-lifeworx-purple-600': props.livingPreference === 'live_in',
                                }
                            ]"
                        />
                    </label>
                    <div
                        v-if="props.livingPreference !== 'live_in'"
                        @click.prevent="() => { state.enable_day[day] == false && enableDays([day]); }"
                        :class="[
                            'flex flex-col items-center gap-1',
                            {
                                'cursor-pointer opacity-50 group-hover:opacity-100': state.enable_day[day] == false
                            }
                        ]"
                    >
                        <LwxCompactSelect
                            :options="hourOptions"
                            v-model="state.start_hour[day]"
                            :disabled="timeDisable || state.enable_day[day] == false"
                        />
                        <LwxCompactSelect
                            :options="hourOptions"
                            v-model="state.end_hour[day]"
                            :disabled="timeDisable || state.enable_day[day] == false"
                        />
                    </div>
                </div>
            </div>
            <div class="flex justify-between">
                <InputLabel
                    for="flexible-fill-in"
                    value="Fill-in"
                    class="text-xs w-fit flex gap-1"
                    palette="blue"
                >
                    <LwxSwitch
                        id="flexible-fill-in"
                        :disabled="scheduleDisable"
                        v-model="state.flexible_fill_in"
                        palette="blue"
                    />
                </InputLabel>

                <InputLabel
                    for="alternate-weekends-only"
                    value="Alt. Weekends"
                    class="text-xs w-fit flex gap-1"
                    palette="blue"
                >
                    <LwxSwitch
                        id="alternate-weekends-only"
                        :disabled="scheduleDisable"
                        v-model="state.alternate_weekends_only"
                        palette="blue"
                    />
                </InputLabel>

                <InputLabel
                    for="exact-match"
                    value="Exact Match"
                    class="text-xs w-fit flex gap-1"
                    palette="blue"
                >
                    <LwxSwitch
                        id="exact-match"
                        :disabled="scheduleDisable"
                        v-model="state.exact_match"
                        palette="blue"
                    />
                </InputLabel>
            </div>
        </div>
        <div v-else class="bg-[#008AAB26] rounded-md p-4 flex items-center justify-center border border-lifeworx-blue-600/30">
            <div class="flex items-center gap-3">
                <button
                    class="text-xs font-bold text-lifeworx-blue-700 hover:text-lifeworx-blue-800 underline underline-offset-2 transition-colors"
                    @click="$emit('update:livingPreference', 'live_in')"
                >
                    Live-In
                </button>
                <span class="text-xs text-stone-400">or</span>
                <button
                    class="text-xs font-bold text-lifeworx-blue-700 hover:text-lifeworx-blue-800 underline underline-offset-2 transition-colors"
                    @click="$emit('update:livingPreference', 'live_out')"
                >
                    Live-Out
                </button>
            </div>
        </div>

        <div
            v-if="state.has_multiple_shifts_per_day"
            class="bg-[#AB210026] border-t-0 rounded-b-md mx-4 p-2 flex items-center justify-center border border-lifeworx-red-500/30 text-xs"
        >
            <p class="text-center"><span class="font-bold text-lifeworx-red-500">Warning:</span> This client has multiple shifts on the same day of week. Adjust schedule search parameters as needed.</p>
        </div>
    </div>

</template>
